import { CSSProperties, RefObject, useEffect, useRef } from 'react';
import { VirtuosoHandle } from 'react-virtuoso';

import { TCustomCarouselScrollTo } from './custom-carousel.types';

type Params = {
  virtuosoRef: RefObject<VirtuosoHandle | null> | null;
  scrollContainerRef: RefObject<HTMLDivElement | null> | null;
  scrollTo?: TCustomCarouselScrollTo;
  virtualized?: boolean;
  firstItemRendered?: boolean;
  lastItemRendered?: boolean;
  gap?: CSSProperties['gap'];
};

const useScrollToIndex = ({
  gap,
  scrollTo,
  virtuosoRef,
  scrollContainerRef,
  virtualized,
  firstItemRendered,
  lastItemRendered,
}: Params) => {
  const scrolledToIndex = useRef<number | null>(null);

  useEffect(() => {
    if (!scrollTo) {
      scrolledToIndex.current = null;
      return;
    }

    const scrollContainer = scrollContainerRef?.current;
    const scrollToIndex = scrollTo.index;
    const align = scrollTo.align || 'center';
    const behavior = scrollTo?.behavior || 'smooth';
    // zero is falsy, so we specifically check for undefined
    if (!scrollContainer || scrollToIndex === undefined) {
      return;
    }
    if (scrolledToIndex.current === scrollToIndex) {
      return;
    }

    if (virtualized) {
      if (!firstItemRendered) {
        return;
      }

      const virtuoso = virtuosoRef?.current;
      if (!virtuoso) {
        return;
      }

      // Note: we default with smooth scroll here, but be warned that it can kill react-virtuoso's performance
      // and potentially clash with loading strategies. If we find it becomes problematic we can change the default to smooth
      // so far I haven't noticed any issues, but should keep it in mind
      virtuoso.scrollToIndex({ index: scrollToIndex, behavior, align });

      scrolledToIndex.current = scrollToIndex;
    } else {
      if (!lastItemRendered) {
        return;
      }

      const itemToScrollTo = scrollContainer.querySelector(`[data-index="${scrollToIndex}"]`);
      const boundingRect = itemToScrollTo?.getBoundingClientRect();
      const itemScrollLeftPosition = boundingRect?.x ?? 0;
      const itemWidth = boundingRect?.width ?? 0;
      const gapAdjust = gap ? parseInt(gap.toString()) : 0;

      let scrollAdjustmentByPlacement = 0;
      if (align === 'start') {
        scrollAdjustmentByPlacement = 0;
      }
      if (align === 'center') {
        scrollAdjustmentByPlacement = scrollContainer.clientWidth / 2 - itemWidth / 2;
      }
      if (align === 'end') {
        scrollAdjustmentByPlacement = scrollContainer.clientWidth - itemWidth - gapAdjust;
      }
      const scrollAmount = itemScrollLeftPosition - scrollAdjustmentByPlacement;

      scrolledToIndex.current = scrollToIndex;

      scrollContainer.scrollTo({
        left: scrollAmount,
        behavior,
      });
    }
  }, [scrollTo, virtualized, firstItemRendered, lastItemRendered, gap, scrollContainerRef, virtuosoRef]);
};

export default useScrollToIndex;
